import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ImageButton from "@components/button/ImageButton";
import LanguageButton from "@components/button/LanguageButton";
import { useNavigate } from "react-router-dom";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import useAuthenticated from "@hook/useAuth";

const HeaderContainer = styled.header(
  () =>
    ({
      position: "sticky",
      top: 0,
      zIndex: 1000,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: "56px",
      padding: "2.1rem 2rem",
    }) as const,
);

const LogoWrapper = styled.div({
  paddingBottom: "2px",
  cursor: "pointer",
});

const MenuWrapper = styled.div({
  display: "flex",
  gap: "1rem",
});

const PositionWrapper = styled.div({
  position: "relative",
});

const CartAlarm = styled.div(({ theme }) => ({
  position: "absolute",
  top: "-0.2rem",
  right: "-0.2rem",
  backgroundColor: theme.color.mint,
  borderRadius: "50%",
  width: "16px",
  height: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.color.black,
  fontSize: "12px",
  fontWeight: theme.fontWeight.semiBold,
  padding: "0.4rem",
  cursor: "pointer",
}));

const HasAlarm = styled.div(({ theme }) => ({
  position: "absolute",
  top: "0rem",
  right: "0.1rem",
  backgroundColor: theme.color.mint,
  borderRadius: "50%",
  width: "10px",
  height: "10px",
}));

export default function Header() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getCart, getActivityAlarmList, getNewsList } = useApiRequest();

  const { isAuthenticated } = useAuthenticated();

  const { data: cartList } = useQuery({
    queryKey: ["cartList"],
    queryFn: getCart,
    enabled: isAuthenticated,
  });

  const navigateHome = () => {
    navigate("/");
  };

  const { data: activityAlarmList } = useQuery({
    queryKey: ["activityAlarmList"],
    queryFn: getActivityAlarmList,
    enabled: isAuthenticated,
  });

  const { data: newsList } = useQuery({
    queryKey: ["newsList"],
    queryFn: getNewsList,
    enabled: isAuthenticated,
  });

  // 두 리스트 모두에서 읽지 않은 알림이 있는지 확인
  const hasUnreadNotification =
    activityAlarmList?.some((noti: any) => !noti.is_read) ||
    newsList?.some((news: any) => !news.is_read);

  // 모든 상품 개수
  const totalProductCount = cartList?.reduce(
    (total: any, seller: any) => total + seller.product_list.length,
    0,
  );

  return (
    <HeaderContainer>
      <LogoWrapper onClick={navigateHome}>
        <Image
          src={theme.image.logo_black.default}
          width="140px"
          height="100%"
        />
      </LogoWrapper>

      <MenuWrapper>
        <PositionWrapper>
          <ImageButton
            src={theme.image.shopping_bag.default}
            width="28px"
            height="28px"
            onClick={() => {
              navigate("/cart");
            }}
          />

          {totalProductCount > 0 && <CartAlarm>{totalProductCount}</CartAlarm>}
        </PositionWrapper>

        <PositionWrapper>
          <ImageButton
            src={theme.image.noti.default}
            width="28px"
            height="28px"
            onClick={() => {
              navigate("/alarm");
            }}
          />
          {hasUnreadNotification && <HasAlarm />}
        </PositionWrapper>

        <LanguageButton />
      </MenuWrapper>
    </HeaderContainer>
  );
}
